import { cn } from "@omni/utils/styles";

import { Anchor } from "@/components/General/Anchor/anchor.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type SafetyDocumentsProps = {
    signalWord?: string | null;
    safetyIcons: Array<any>;
    hazardStatements: string;
    precautionaryStatements: string;
    sdsUrl?: string | null;
    dopUrl?: string | null;
    isReadMoreDataAvailable: boolean;
};

const SafetyDocuments = ({
    signalWord,
    safetyIcons,
    hazardStatements,
    precautionaryStatements,
    sdsUrl,
    dopUrl,
    isReadMoreDataAvailable
}: SafetyDocumentsProps) => {
    const { t } = useGranite();

    return (
        <div className={cn("js-product-features tw:mb-4")}>
            <section className={cn("tw:mb-3 js-product-features")}>
                <Col className={cn("tw:border-b tw:border-primary")}>
                    <div className={cn("tw:py-2 tw:font-bold")}>
                        {t("productDetail.safetyList.title")}
                    </div>
                </Col>
                <div>
                    {signalWord && (
                        <Col>
                            <Row className={cn("tw:border-b tw:border-border")}>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-grey")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        {t(
                                            "productDetail.safetyList.signalWord"
                                        )}
                                    </div>
                                </Col>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-background")}
                                >
                                    <div
                                        className={cn("tw:py-2")}
                                        dangerouslySetInnerHTML={{
                                            __html: signalWord
                                        }}
                                    ></div>
                                </Col>
                            </Row>
                        </Col>
                    )}
                    {safetyIcons.length > 0 && (
                        <Col>
                            <Row className={cn("tw:border-b tw:border-border")}>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-grey")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        {t(
                                            "productDetail.safetyList.safetyIcons"
                                        )}
                                    </div>
                                </Col>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-background")}
                                >
                                    <Row className={cn("tw:py-2")}>
                                        {safetyIcons.map(
                                            (safetyIcon, index) => {
                                                return (
                                                    <Col
                                                        sm={3}
                                                        key={`safety-${index}`}
                                                    >
                                                        <img
                                                            src={safetyIcon.url}
                                                            alt={
                                                                safetyIcon.altText
                                                            }
                                                        />
                                                    </Col>
                                                );
                                            }
                                        )}
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                    )}
                    {!!hazardStatements && (
                        <Col>
                            <Row className={cn("tw:border-b tw:border-border")}>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-grey")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        {t(
                                            "productDetail.safetyList.hazardStatements"
                                        )}
                                    </div>
                                </Col>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-background")}
                                >
                                    <div
                                        className={cn("tw:py-2")}
                                        dangerouslySetInnerHTML={{
                                            __html: hazardStatements
                                        }}
                                    ></div>
                                </Col>
                            </Row>
                        </Col>
                    )}
                    {!!precautionaryStatements && (
                        <Col
                            className={cn(
                                "js-product-features__list--overflow tw:hidden"
                            )}
                        >
                            <Row className={cn("tw:border-b tw:border-border")}>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-grey")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        {t(
                                            "productDetail.safetyList.precautionaryStatements"
                                        )}
                                    </div>
                                </Col>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-background")}
                                >
                                    <div
                                        className={cn("tw:py-2")}
                                        dangerouslySetInnerHTML={{
                                            __html: precautionaryStatements
                                        }}
                                    ></div>
                                </Col>
                            </Row>
                        </Col>
                    )}
                    {!!sdsUrl && (
                        <Col
                            className={cn(
                                "js-product-features__list--overflow tw:hidden"
                            )}
                        >
                            <Row className={cn("tw:border-b tw:border-border")}>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-grey")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        {t(
                                            "productDetail.safetyList.sds.title"
                                        )}
                                    </div>
                                </Col>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-background")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        <a
                                            href={sdsUrl}
                                            target="_blank"
                                            className="btn-link"
                                            rel="noopener"
                                        >
                                            {t(
                                                "productDetail.safetyList.sds.link"
                                            )}
                                        </a>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    )}
                    {!!dopUrl && (
                        <Col
                            className={cn(
                                "js-product-features__list--overflow tw:hidden"
                            )}
                        >
                            <Row className={cn("tw:border-b tw:border-border")}>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-grey")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        {t(
                                            "productDetail.safetyList.dop.title"
                                        )}
                                    </div>
                                </Col>
                                <Col
                                    sm={6}
                                    className={cn("tw:bg-background")}
                                >
                                    <div className={cn("tw:py-2")}>
                                        <a
                                            href={dopUrl}
                                            target="_blank"
                                            className="btn-link"
                                            rel="noopener"
                                        >
                                            {t(
                                                "productDetail.safetyList.dop.link"
                                            )}
                                        </a>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    )}
                </div>
                {isReadMoreDataAvailable && (
                    <Col className={cn("tw:my-2")}>
                        <Anchor
                            color={"link"}
                            href={"#"}
                            className={cn(
                                "js-product-feature-list__link--less tw:hidden"
                            )}
                        >
                            {t("productDetail.featureList.link.showLess")}{" "}
                            <i className={cn("las la-angle-up tw:ml-1")}></i>
                        </Anchor>
                        <Anchor
                            color={"link"}
                            href={"#"}
                            className={cn(
                                "js-product-feature-list__link--more"
                            )}
                        >
                            {t("productDetail.featureList.link.showMore")}{" "}
                            <i className={cn("las la-angle-down tw:ml-1")}></i>
                        </Anchor>
                    </Col>
                )}
            </section>
        </div>
    );
};

export { SafetyDocuments };
